import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Plus, Pencil, Trash2, Navigation, Phone } from 'lucide-react';
import { authedAxios, wazeUrl, DAY_LABELS, FREQ_LABELS } from './api';
import CustomerFormDialog from './CustomerFormDialog';

export default function CustomersView() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await authedAxios().get('/admin/customers');
      setCustomers(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (payload) => {
    if (editing) {
      await authedAxios().put(`/admin/customers/${editing.id}`, payload);
    } else {
      await authedAxios().post('/admin/customers', payload);
    }
    await load();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remove ${c.name}? Upcoming scheduled jobs will also be removed.`)) return;
    await authedAxios().delete(`/admin/customers/${c.id}`);
    await load();
  };

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) ||
           c.address.toLowerCase().includes(q) ||
           c.phone.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Input
          placeholder="Search by name, address, phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1"
          data-testid="customer-search"
        />
        <Button
          onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
          data-testid="add-customer-btn"
        >
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-600">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">
          {customers.length === 0 ? 'No customers yet. Tap "Add" to add your first one.' : 'No matches.'}
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="customers-list">
          {filtered.map((c) => (
            <Card key={c.id} data-testid={`customer-card-${c.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900">{c.name}</p>
                    <p className="text-sm text-gray-600 truncate">{c.address}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(c); setDialogOpen(true); }} data-testid={`edit-customer-${c.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleDelete(c)} data-testid={`delete-customer-${c.id}`}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="secondary">{FREQ_LABELS[c.frequency]}</Badge>
                  <Badge variant="secondary">{DAY_LABELS[c.day_of_week]} {c.time_of_day}</Badge>
                  <Badge className="bg-green-100 text-green-800 border-0">${(c.price || 0).toFixed(0)}</Badge>
                  {!c.active && <Badge variant="destructive">Inactive</Badge>}
                </div>
                {c.notes && <p className="text-xs text-gray-500 bg-yellow-50 p-2 rounded">{c.notes}</p>}
                <div className="grid grid-cols-2 gap-2">
                  <a href={wazeUrl(c.address)} target="_blank" rel="noreferrer">
                    <Button className="w-full bg-cyan-500 hover:bg-cyan-600 text-white text-sm" type="button" data-testid={`waze-customer-${c.id}`}>
                      <Navigation className="h-4 w-4 mr-1" /> Waze
                    </Button>
                  </a>
                  <a href={`tel:${c.phone}`}>
                    <Button className="w-full bg-gray-800 hover:bg-gray-900 text-white text-sm" type="button" data-testid={`call-customer-${c.id}`}>
                      <Phone className="h-4 w-4 mr-1" /> Call
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CustomerFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleSubmit}
        initial={editing}
      />
    </div>
  );
}
